/**
 * The NexERP mark and wordmark, and the tile each app is known by.
 *
 * NexERP Brand Guidelines (v1.0 / September 2026):
 * - Primary Logo Gradient: #1959C8 to #24C3EA
 * - Endorsement Subtitle: Bitstek
 * - Name: NexERP
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icon.jsx';

export function BrandMark({ size = 28, className = '' }) {
  const id = useId();
  const gradient = `nex-mark-${id.replace(/[^a-zA-Z0-9-]/g, '')}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={`brand-mark-svg ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1959c8" />
          <stop offset="1" stopColor="#24c3ea" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill={`url(#${gradient})`} />
      <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" fill="none" stroke="rgba(255,255,255,0.22)" />
      <path
        d="M10.5 22.5V10.5l10.5 12V12.8"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="21" cy="9.6" r="2.3" fill="#ffffff" />
    </svg>
  );
}

/** Mark + "NexERP" + "Bitstek". `compact` shows the mark alone. */
export function Brand({ compact = false, size = 26, showEndorsement = true }) {
  const { t } = useTranslation('navigation');
  return (
    <span className="brand" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <BrandMark size={size} />
      {!compact && (
        <span className="brand-word" style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.15 }}>
          <span style={{ fontSize: size >= 28 ? '16px' : '14px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--c-text)' }}>
            Nex<span className="brand-word-strong" style={{ color: 'var(--nex-blue, #1677d2)' }}>ERP</span>
          </span>
          {showEndorsement && (
            <span style={{ fontSize: '9px', color: 'var(--c-text-3)', fontWeight: 500, letterSpacing: '0.02em', opacity: 0.85 }}>
              Bitstek
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/***
 * An app's icon tile: its identity hue as a soft gradient, its glyph in white.
 * `app` needs `key` and `icon`; sizes are sm (22), md (30), lg (40), xl (52).
 */
export function AppIcon({ app, size = 'md' }) {
  const glyph = { sm: 13, md: 16, lg: 21, xl: 26 }[size] ?? 16;
  return (
    <span className={`app-icon app-icon-${size}`} data-app={app?.key ?? 'home'} aria-hidden="true">
      <Icon name={app?.icon ?? 'home'} size={glyph} strokeWidth={size === 'sm' ? 2 : 1.9} />
    </span>
  );
}
